import {useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {Trans, useTranslation} from 'react-i18next'
import {dismissDntFlash, useConsent, useDntFlash} from '../../common/cookieConsent/store'
import {writeConsent} from '../../common/cookieConsent/sync'
import {trackEvent} from '../../common/analytics/trackEvent'
import './CookieConsentBanner.css'

// Global, plain-HTML bottom-of-page banner. Shows when the user has not made an explicit choice
// (LS undecided) OR when Do Not Track triggered an implicit reject — in that case the banner
// renders an informational variant. The banner is portaled to <body> so it floats above any
// layout (editor canvas, sticky header) regardless of route.
//
// The banner is mounted OUTSIDE the React Router tree (in main.tsx, above <App />), so it must
// not use any react-router hooks or components — a single <Link> here crashes on first paint
// in incognito (no Router context). Plain <a href> is intentional: navigating to /cookies once
// per visitor is a full page load, which is fine and keeps the banner usable on every route.

const CookieConsentBanner = () => {
  const {t} = useTranslation()
  // Two independent stores: consent state and the DNT flash flag. Both subscribe through the
  // same listener set, but each has its own snapshot — so dismissing the DNT flash (which
  // doesn't touch consent) still triggers a re-render here.
  const consent = useConsent()
  const dntActive = useDntFlash()
  const [visible, setVisible] = useState(false)

  // Defer first render so we never compete with R3F bootstrap or the initial route paint.
  useEffect(() => {
    const id = window.setTimeout(() => setVisible(true), 0)
    return () => window.clearTimeout(id)
  }, [])

  if (!visible) return null
  // Hide once the user makes any explicit choice. DNT flash variant takes precedence so the
  // user still sees an info notice the first time we honor their browser-level signal.
  const shouldShow = dntActive || consent === null
  if (!shouldShow) return null

  // Reject + DNT-dismiss paths are intentionally NOT tracked: the user is rejecting analytics,
  // so firing a tracking event would be self-contradictory. Accept fires AFTER writeConsent so
  // GA has flipped `enabled = true` by the time trackEvent runs (the listener chain is synchronous).
  const handleAcceptAll = () => {
    writeConsent(true, 'banner')
    trackEvent('cookie_banner_accepted', {source: 'banner'})
  }
  const handleRejectAll = () => writeConsent(false, 'banner')
  const handleDismissDnt = () => dismissDntFlash()

  const body = (
    <div className="cookie-banner-root" role="dialog" aria-live="polite" aria-label={t('cookieBanner.aria')}>
      <div className="cookie-banner-card">
        {dntActive ? (
          <>
            <p className="cookie-banner-body">
              <Trans
                i18nKey="cookieBanner.dntBody"
                components={{policy: <a href="/cookies" className="cookie-banner-link" />}}
              />
            </p>
            <div className="cookie-banner-actions">
              <button type="button" className="cookie-banner-btn primary" onClick={handleDismissDnt}>
                {t('cookieBanner.ok')}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="cookie-banner-body">{t('cookieBanner.body')}</p>
            <div className="cookie-banner-actions">
              <button type="button" className="cookie-banner-btn primary" onClick={handleAcceptAll}>
                {t('cookieBanner.acceptAll')}
              </button>
              <button type="button" className="cookie-banner-btn ghost" onClick={handleRejectAll}>
                {t('cookieBanner.rejectAll')}
              </button>
              <a href="/cookies" className="cookie-banner-link">
                {t('cookieBanner.cookiePolicy')}
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  )

  return createPortal(body, document.body)
}

export default CookieConsentBanner
